<template>
    <div    class="w-full flex justify-center">
        <div    class="w-1/2 flex flex-col justify-center">
            <div    class="w-full flex justify-center">
                <h2     class="text-center">Create User</h2>
            </div>

            <!-- Basic User info -->
            <div    class="w-full text-lg font-semibold flex my-4 pb-2 border-sold border-b border-gray-200">
                User Information
            </div>

            <!-- Company Id -->
            <div    class="w-full flex flex-col mb-4">
                <span   class="font-hairline">User Company</span>
                <select2    v-model:value="companyId"
                            placeholder="Company"
                            :options="companiesList"
                            :allow-clear="false"
                            :close-on-select="true"
                            :required="true"
                ></select2>
            </div>

            <!-- First Name -->
            <div    class="w-full flex">
                <floating-input     v-model:value="firstName"
                                    label="First Name"
                                    id="first_name"
                                    class="w-full"
                                    :class="[hasErrorFirstName ? 'border border-red-500' : 'border-0']"
                                    @change="clearError('first_name')"
                ></floating-input>
            </div>

            <!-- Last Name -->
            <div    class="w-full flex">
                <floating-input     v-model:value="lastName"
                                    label="Last Name"
                                    id="last_name"
                                    class="w-full"
                                    :class="[hasErrorLastName ? 'border border-red-500' : 'border-0']"
                                    @change="clearError('last_name')"
                ></floating-input>
            </div>

            <!-- Email -->
            <div    class="w-full flex">
                <floating-input     v-model:value="email"
                                    label="Email"
                                    id="email"
                                    class="w-full"
                                    :class="[hasErrorEmail ? 'border border-red-500' : 'border-0']"
                                    @change="clearError('email')"
                ></floating-input>
            </div>

            <!-- Phone Number -->
            <div    class="w-full flex">
                <floating-input     v-model:value="phoneNumber"
                                    label="Phone Number (XXXXXXXXXX) [optional]"
                                    id="phone_number"
                                    class="w-full"
                ></floating-input>
            </div>

            <!-- Permission Level -->
            <div    class="w-full flex text-lg font-semibold mt-4 pb-2 border-sold border-b border-gray-200">
                Permission level
            </div>
            <permission-level-radio     v-model="permissionLevel"
                                        :permission-levels="permissionLevels"
            ></permission-level-radio>

            <!-- Save -->
            <banner-error v-model:isOpen="hasError">
                <template #title>Error Adding User</template>
                <template #content>
                    <span   v-if="hasErrorFirstName" class="py-2">First Name is Required.</span>
                    <span   v-if="hasErrorLastName" class="py-2">Last Name is Required.</span>
                    <span   v-if="hasErrorEmail" class="py-2">Email is Required and must be a valid email.</span>
                    <span   v-if="emailIsDeleted" class="py-2">
                    This email address has been previously used and removed from the system.
                    Please contact support to re-activate this user.
                </span>
                    <span   v-if="hasErrorPermissionLevel" class="py-2">
                    Unable to create a user of that permission on this company.  Please contact a user with
                    sufficient permissions.
                </span>
                    <span   v-if="hasErrorUserExists" class="py-2">
                    User Already exists on this site.  Please continue with the next user.
                </span>
                </template>
            </banner-error>
            <button-blue-primary        @click="createUser"
            >
                <div    v-if="creatingUser"
                        class="flex items-center">
                    <span   :class="iconLoading"></span>
                    <span>Creating User</span>
                </div>

                <div    v-else>
                    <span>Create User</span>
                </div>
            </button-blue-primary>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import FloatingInput from "@/components/elements/FloatingInput";
import Select2 from "@/components/elements/select2"
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary";
import PermissionLevelRadio from "@/components/Users/PermissionLevelRadio";
import BannerError from "@/components/Banners/BannerError.vue";

export default {
    name: "CreateUser",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {BannerError, PermissionLevelRadio, ButtonBluePrimary, FloatingInput, Select2},
    props: {
        companiesList: Object,
        permissionLevels: Array,
    },
    data() {
        return {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            companyId: '',
            permissionLevel: 'ViewOnly-Old',

            creatingUser: false,
            errors: null,
        }
    },
    created() {
      let temporaryCompanyId = this.company.getCompanyId()
      if(temporaryCompanyId == 'sonicu'){
        this.companyId = null;
      } else {
        this.companyId = temporaryCompanyId;
      }
    },
    mounted() {
        
    },
    methods: {
        createUser() {
            if (this.creatingUser) {
                return
            }
            if(this.companyId == null){
              window.alert('No company selected -- please select a company to create the user');
              return;
            }
            // Detect if the companyId is 'sonicu', if it is, the email must end in 'sonicu.com' or we should throw an alert
          if(this.companyId == 'sonicu'){
            if(this.email.indexOf('sonicu.com') == -1){
              window.alert('Email must end in sonicu.com for sonicu company users -- you have likely selected the wrong company for this user');
              return;
            }
          }

            this.creatingUser = true
            this.errors = null
            axios.post(this.company.buildCompanyApiInternalRoute(`users`), {
                company_id: this.companyId,
                first_name: this.firstName,
                middle_initial: '',
                last_name: this.lastName,
                email: this.email,
                email_confirmation: this.email,
                phone_number: this.phoneNumber,
                permission_level: this.permissionLevel
            }).then(response => {
                this.redirect(response.data.email)
            }).catch(error => {
                console.log(error.response.data)
                this.errors = error.response.data
            }).then(() => {
                this.creatingUser = false
            })
        },
        redirect(email) {
            window.location.href = this.company.buildCompanyRoute('creating-user') + `?email=${email}`
        },
        clearError(error) {
            if (error === 'first_name' && this.hasErrorFirstName) {
                this.errors['first_name'] = false
            }
            if (error === 'last_name' && this.hasErrorLastName) {
                this.errors['last_name'] = false
            }
            if (error === 'email' && this.hasErrorEmail) {
                this.errors['email'] = false
            }
            if (error === 'permission_error' && this.hasErrorPermissionLevel) {
                this.errors['permission_error'] = false
            }
            if (error === 'user_exists' && this.hasErrorUserExists) {
                this.errors['user_exists'] = false
            }
            if (error === 'email_deleted' && this.emailIsDeleted) {
                this.errors['email_deleted'] = false
            }
            this.$forceUpdate()
        },
    },
    computed: {
        hasErrorFirstName() {
            return this.hasError && Object.keys(this.errors).indexOf('first_name') !== -1 && this.errors['first_name'] === true
        },
        hasErrorLastName() {
            return this.hasError && Object.keys(this.errors).indexOf('last_name') !== -1 && this.errors['last_name'] === true
        },
        hasErrorEmail() {
            return this.hasError && Object.keys(this.errors).indexOf('email') !== -1 && this.errors['email'] === true
        },
        emailIsDeleted() {
            return this.hasError && Object.keys(this.errors).indexOf('email_deleted') !== -1 && this.errors['email_deleted'] === true
        },
        hasErrorPermissionLevel() {
            return this.hasError
                && Object.keys(this.errors).indexOf('permission_error') !== -1
                && this.errors['permission_error'] === true
        },
        hasErrorUserExists() {
            return this.hasError
                && Object.keys(this.errors).indexOf('user_exists') !== -1
                && this.errors['user_exists'] === true
        },
        hasError() {
            return !_.isNil(this.errors)
        },
    },
    watch: {

    },
}
</script>

<style scoped>

</style>